/**
 * Utility functions for detecting and manipulating authentication headers
 * in HTTP request text for the Replay tab feature
 */

import { TOKEN_BEARING_HEADERS } from '@terra/shared-utils';

export {
  TOKEN_BEARING_HEADERS,
  JWT_SYNC_HEADERS,
  buildCookieHeader,
  extractAuthScheme,
  preserveAuthScheme,
  isJwt,
} from '@terra/shared-utils';

// Common authentication-related header names (case-insensitive)
const AUTH_HEADER_NAMES = [
  'authorization',
  'cookie',
  'x-auth-token',
  'x-csrf-token',
  'x-xsrf-token',
  'x-session-id',
  'x-access-token',
  'x-api-key',
  'api-key',
  'bearer',
  'session',
  'set-cookie',
  'x-request-id',
  'x-correlation-id',
];
const SESSION_HEADERS = ['cookie', 'x-session-id', 'session', 'set-cookie'];

// Headers that typically contain CSRF tokens
const CSRF_HEADERS = ['x-csrf-token', 'x-xsrf-token'];

export type AuthHeaderType = 'token' | 'cookie' | 'csrf' | 'other';

export type ParsedHeader = {
  name: string;
  value: string;
  lineIndex: number;
  startOffset: number;
  endOffset: number;
  type: AuthHeaderType;
};

/**
 * Check if a header name is an authentication-related header
 */
export function isAuthHeader(headerName: string): boolean {
  return AUTH_HEADER_NAMES.includes(headerName.toLowerCase());
}

/**
 * Get the type of authentication header
 */
export function getAuthHeaderType(headerName: string): AuthHeaderType {
  const lowerName = headerName.toLowerCase();
  if (TOKEN_BEARING_HEADERS.includes(lowerName)) return 'token';
  if (SESSION_HEADERS.includes(lowerName)) return 'cookie';
  if (CSRF_HEADERS.includes(lowerName)) return 'csrf';
  return 'other';
}

/**
 * Parse a single line to check if it's a header line and extract header info
 */
export function parseHeaderLine(
  line: string,
  lineIndex: number,
  lineStartOffset: number,
): ParsedHeader | null {
  // Skip empty lines and request line (e.g., "GET /path HTTP/1.1")
  if (!line.trim() || line.match(/^[A-Z]+\s+/)) {
    return null;
  }

  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) {
    return null;
  }

  const name = line.slice(0, colonIndex).trim();
  const value = line.slice(colonIndex + 1).trim();

  // Validate header name (should be alphanumeric with dashes)
  if (!name.match(/^[A-Za-z][A-Za-z0-9-]*$/)) {
    return null;
  }

  return {
    name,
    value,
    lineIndex,
    startOffset: lineStartOffset,
    endOffset: lineStartOffset + line.length,
    type: isAuthHeader(name) ? getAuthHeaderType(name) : 'other',
  };
}

/**
 * Find all headers in HTTP request text
 */
export function parseHeaders(text: string): ParsedHeader[] {
  const lines = text.split('\n');
  const headers: ParsedHeader[] = [];
  let offset = 0;
  let inBody = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Empty line marks the start of body
    if (line.trim() === '' && i > 0) {
      inBody = true;
    }

    if (!inBody) {
      const header = parseHeaderLine(line, i, offset);
      if (header) {
        headers.push(header);
      }
    }

    offset += line.length + 1; // +1 for newline
  }

  return headers;
}

/**
 * Find all authentication headers in HTTP request text
 */
export function findAuthHeaders(text: string): ParsedHeader[] {
  return parseHeaders(text).filter((header) => isAuthHeader(header.name));
}

/**
 * Find the header at a specific line index
 */
export function getHeaderAtLine(
  text: string,
  lineIndex: number,
): ParsedHeader | null {
  const headers = parseHeaders(text);
  return headers.find((h) => h.lineIndex === lineIndex) || null;
}

/**
 * Replace a header value in the request text
 */
export function replaceHeaderValue({
  text,
  headerName,
  newValue,
}: {
  text: string;
  headerName: string;
  newValue: string;
}): string {
  const lines = text.split('\n');
  const headerNameLower = headerName.toLowerCase();

  const updatedLines = lines.map((line) => {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return line;

    const name = line.slice(0, colonIndex).trim();
    if (name.toLowerCase() === headerNameLower) {
      return `${name}: ${newValue}`;
    }
    return line;
  });

  return updatedLines.join('\n');
}

/**
 * Replace multiple headers at once
 */
export function replaceHeaders({
  text,
  replacements,
}: {
  text: string;
  replacements: Record<string, string>;
}): string {
  let result = text;
  for (const [headerName, newValue] of Object.entries(replacements)) {
    result = replaceHeaderValue({ text: result, headerName, newValue });
  }
  return result;
}

/**
 * Remove a header from the request text
 */
export function removeHeader({
  text,
  headerName,
}: {
  text: string;
  headerName: string;
}): string {
  const headerNameLower = headerName.toLowerCase();
  const lines = text.split('\n');
  let inBody = false;
  const filtered = lines.filter((line, index) => {
    if (!inBody && line.trim() === '' && index > 0) {
      inBody = true;
    }
    if (inBody) return true;
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return true;
    const name = line.slice(0, colonIndex).trim();
    return name.toLowerCase() !== headerNameLower;
  });
  return filtered.join('\n');
}

/**
 * Add or update a header in the request text
 */
export function setHeader({
  text,
  headerName,
  value,
}: {
  text: string;
  headerName: string;
  value: string;
}): string {
  const headers = parseHeaders(text);
  const existingHeader = headers.find(
    (h) => h.name.toLowerCase() === headerName.toLowerCase(),
  );

  if (existingHeader) {
    return replaceHeaderValue({ text, headerName, newValue: value });
  }

  // Add new header after the last header line
  const lines = text.split('\n');
  let lastHeaderIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '' && i > 0) break; // Hit body separator
    if (line.includes(':') && !line.match(/^[A-Z]+\s+/)) {
      lastHeaderIndex = i;
    }
  }

  if (lastHeaderIndex === -1) {
    // No headers found, add after first line (request line)
    lastHeaderIndex = 0;
  }

  lines.splice(lastHeaderIndex + 1, 0, `${headerName}: ${value}`);
  return lines.join('\n');
}

export function getLineIndexFromOffset(text: string, offset: number): number {
  const beforeOffset = text.slice(0, offset);
  return beforeOffset.split('\n').length - 1;
}
